import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { ShieldCheck, ShieldX, Clock, Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import type { AdminProducer } from '@/data/producers/types';
import { getVerificationStatus } from '@/data/producers/types';

interface AdminVerificationSectionProps {
  producer: AdminProducer;
  onUpdate: (id: string, updates: Partial<AdminProducer>) => Promise<void>;
  onRefresh?: () => void;
}

export function AdminVerificationSection({ producer, onUpdate, onRefresh }: AdminVerificationSectionProps) {
  const { toast } = useToast();
  const [isSaving, setIsSaving] = useState(false);
  const [note, setNote] = useState(producer.verificationNote || '');

  const verificationStatus = getVerificationStatus({
    verified: producer.verified,
    verification_submitted_at: producer.verificationSubmittedAt,
  });

  const handleToggleVerified = async () => {
    const newVerified = !producer.verified;
    setIsSaving(true);
    try {
      await onUpdate(producer.id, {
        verified: newVerified,
        verificationNote: note || undefined,
      });
      onRefresh?.();
      toast({
        title: newVerified ? 'Producent verifierad' : 'Verifiering borttagen',
        description: newVerified
          ? `${producer.name} är nu verifierad och kan publicera.`
          : `${producer.name} är inte längre verifierad.`,
      });
    } catch {
      toast({
        title: 'Kunde inte uppdatera',
        description: 'Försök igen.',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveNote = async () => {
    setIsSaving(true);
    try {
      await onUpdate(producer.id, { verificationNote: note });
      toast({ title: 'Notering sparad' });
    } catch {
      toast({ title: 'Kunde inte spara', variant: 'destructive' });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <ShieldCheck className="h-4 w-4" />
          Verifiering
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Status */}
        <div className="flex items-center gap-3">
          <span className="text-sm text-muted-foreground">Status:</span>
          {verificationStatus === 'verified' && (
            <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950 dark:text-emerald-300 dark:border-emerald-800">
              <ShieldCheck className="h-3 w-3 mr-1" />
              Verifierad
            </Badge>
          )}
          {verificationStatus === 'pending' && (
            <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950 dark:text-amber-300 dark:border-amber-800">
              <Clock className="h-3 w-3 mr-1" />
              Inskickad för verifiering
            </Badge>
          )}
          {verificationStatus === 'not_submitted' && (
            <Badge variant="outline" className="bg-muted text-muted-foreground">
              <ShieldX className="h-3 w-3 mr-1" />
              Ej verifierad
            </Badge>
          )}
        </div>

        {/* Verified at timestamp */}
        {producer.verifiedAt && (
          <p className="text-xs text-muted-foreground">
            Verifierad: {new Date(producer.verifiedAt).toLocaleDateString('sv-SE', {
              year: 'numeric', month: 'long', day: 'numeric',
            })}
          </p>
        )}

        {/* Toggle button */}
        <Button
          variant={producer.verified ? 'outline' : 'default'}
          size="sm"
          onClick={handleToggleVerified}
          disabled={isSaving}
        >
          {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          {producer.verified ? 'Ta bort verifiering' : 'Markera som verifierad'}
        </Button>

        {/* Note */}
        <div className="space-y-2">
          <Label htmlFor="verification-note" className="text-sm">
            Verifieringsnotering
          </Label>
          <Textarea
            id="verification-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Intern notering om verifieringen..."
            rows={2}
            className="text-sm"
          />
          {note !== (producer.verificationNote || '') && (
            <Button variant="outline" size="sm" onClick={handleSaveNote} disabled={isSaving}>
              Spara notering
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
